'use client';

import { useSession } from '@repo/auth';
import { useIdleMount } from '@repo/web-chrome/lib/use-idle-mount';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';

type ISentryModule = typeof import('@repo/web-chrome/lib/sentry');

function MountedSentry(): null {
  const { data: session } = useSession();
  const locale = useLocale();
  const [sentry, setSentry] = useState<ISentryModule | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('@repo/web-chrome/lib/sentry').then((mod) => {
      if (cancelled) {
        return;
      }
      mod.initSentry();
      setSentry(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    sentry?.setSentryLocale(locale);
  }, [sentry, locale]);

  useEffect(() => {
    if (!sentry) {
      return;
    }
    if (session?.user) {
      sentry.setSentryUser({
        userId: session.user.id ?? '',
        email: session.user.email ?? undefined,
        name: session.user.name ?? undefined,
      });
    } else {
      sentry.setSentryAnonUser();
    }
  }, [sentry, session]);

  return null;
}

export function SentryInit(): React.JSX.Element | null {
  const ready = useIdleMount();
  if (!ready) {
    return null;
  }
  return <MountedSentry />;
}
